import { useCallback, useMemo, useRef, useState, lazy, Suspense } from "react"
import { useNavigate } from "react-router-dom"
import { Form, Formik, FormikProps } from "formik"
import { Check } from "lucide-react"
import { Integration } from "../../types"
import { useAppSelector, useToast } from "../../common/hooks"
import { selectUser } from "../../redux/slices/auth"
import { useSaveIntegrationMutation } from "../../redux/queries"
import { classNames } from "../../common/utils"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { RequireRole } from "../../common/validation"
import { DefaultBtn, PrimaryBtn } from "../../common/ui/buttons"
import {
  coreFieldsSchema,
  colorFieldsSchema,
  getEmptyIntegrationValues
} from "../../common/ui/integrations/form/schema"
import {
  GeneralSettingsSection,
  EmbedTypeSection,
  ModuleConfigurationSection,
  BrandingAnalyticsSection
} from "../../common/ui/integrations/tabs/settings/index"
import { IntegrationContext } from "../../common/ui/integrations/context"
import { buildIntegrationPayload } from "./utils"

const InstallStep = lazy(
  () => import("../../common/ui/integrations/tabs/install-step")
)

const STEPS = [
  { id: "details", label: "Embed details" },
  { id: "configure", label: "Configure" },
  { id: "install", label: "Install" }
]

interface StepIndicatorProps {
  currentStep: number
}

const StepIndicator = ({ currentStep }: StepIndicatorProps) => (
  <nav aria-label="Progress" className="flex items-start justify-center py-6">
    {STEPS.map((step, idx) => {
      const isActive = idx <= currentStep
      return (
        <div key={step.id} className="flex items-start">
          {idx > 0 && (
            <div
              className={classNames(
                "h-0.5 w-8 sm:w-24 lg:w-40 mt-[17px] shrink-0",
                idx < currentStep ? "bg-primary-500" : "bg-neutral-200"
              )}
            />
          )}
          <div className="flex w-24 sm:min-w-28 sm:w-auto flex-col items-center">
            <div
              className={classNames(
                "flex items-center justify-center rounded-full text-sm sm:text-base font-medium",
                "size-[34px] shrink-0",
                isActive
                  ? "bg-primary-500 text-white"
                  : "border-2 border-neutral-300 bg-white text-neutral-700"
              )}
            >
              {idx < currentStep ? (
                <Check size={16} strokeWidth={3} />
              ) : (
                idx + 1
              )}
            </div>
            <div className="mt-1.5 flex flex-col items-center text-center">
              <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Step {idx + 1}
              </span>
              <span
                className={classNames(
                  "text-xs sm:text-base font-medium",
                  idx > currentStep ? "text-neutral-400" : "text-neutral-800"
                )}
              >
                {step.label}
              </span>
            </div>
          </div>
        </div>
      )
    })}
  </nav>
)

const StepTwoSections = () => (
  <div className="flex flex-col gap-5">
    <ModuleConfigurationSection title="Select the embed(s) you want to activate" />
    <BrandingAnalyticsSection includeAnalytics={false} />
  </div>
)

const IntegrationNewPage = () => {
  const navigate = useNavigate()
  const { pushSuccessToast, pushErrorToast } = useToast()
  const user = useAppSelector(selectUser)

  const formRef = useRef<FormikProps<Integration>>(null)
  const [step, setStep] = useState(0)
  const [createdIntegrationId, setCreatedIntegrationId] = useState<string>()

  const [saveIntegration, { isLoading: isSaving }] =
    useSaveIntegrationMutation()

  const ownerId = user?.user.id ?? ""
  const initialValues: Integration = useMemo(
    () => getEmptyIntegrationValues(ownerId),
    [ownerId]
  )

  const validateStep = useCallback(async () => {
    if (!formRef.current) return false
    const values = formRef.current.values
    try {
      if (step === 0) {
        await coreFieldsSchema.validate(values, { abortEarly: false })
      } else if (step === 1) {
        await colorFieldsSchema.validate(values, { abortEarly: false })
      }
      return true
    } catch {
      const touchedFields =
        step === 0
          ? { title: true, url: true, type: true, owner: true }
          : { accent_color: true, secondary_accent_color: true }
      formRef.current.setTouched(touchedFields)
      await formRef.current.validateForm()
      return false
    }
  }, [step])

  const handleNext = useCallback(async () => {
    const valid = await validateStep()
    if (valid) setStep(s => s + 1)
  }, [validateStep])

  const handleBack = useCallback(() => {
    setStep(s => Math.max(0, s - 1))
  }, [])

  const handleCancel = useCallback(() => {
    navigate("/integrations")
  }, [navigate])

  const renderActions = () => {
    if (step === 0)
      return (
        <>
          <DefaultBtn onClick={handleCancel}>Cancel</DefaultBtn>
          <PrimaryBtn onClick={handleNext}>Continue</PrimaryBtn>
        </>
      )
    if (step === 1)
      return (
        <>
          <DefaultBtn onClick={handleBack}>Back</DefaultBtn>
          <DefaultBtn onClick={handleCancel}>Cancel</DefaultBtn>
          <PrimaryBtn type="submit" isLoading={isSaving} disabled={isSaving}>
            Create Integration
          </PrimaryBtn>
        </>
      )
    return <PrimaryBtn onClick={handleCancel}>Close</PrimaryBtn>
  }

  const handleCreate = useCallback(async () => {
    if (!formRef.current) return
    const valid = await validateStep()
    if (!valid) return

    const values = formRef.current.values
    const postValues = buildIntegrationPayload(values)
    delete postValues.id

    try {
      const result = await saveIntegration(postValues).unwrap()
      pushSuccessToast("Integration has been created successfully")
      setCreatedIntegrationId(
        "integration_id" in result ? result.integration_id : undefined
      )
      setStep(2)
    } catch {
      pushErrorToast("Failed to create integration")
    }
  }, [validateStep, saveIntegration, pushSuccessToast, pushErrorToast])

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <div className="min-h-screen bg-[#F1F3FA]">
        <div className="bg-white border-b border-neutral-200 px-6 py-5">
          <h1 className="font-serif text-2xl text-neutral-900">
            Add New Integration
          </h1>
        </div>

        <div className="bg-white mx-6 mt-5 rounded-md">
          <StepIndicator currentStep={step} />
        </div>

        <IntegrationContext.Provider value={{ formIntegrationRef: formRef }}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleCreate}
            innerRef={formRef}
            validationSchema={step === 0 ? coreFieldsSchema : colorFieldsSchema}
            validateOnBlur
            enableReinitialize={false}
          >
            <Form>
              <div className="px-6 pb-6 mt-5">
                {step === 0 && (
                  <div className="flex flex-col gap-5">
                    <GeneralSettingsSection
                      titleLabel="Integration Title"
                      urlLabel="Destination URL"
                    />
                    <EmbedTypeSection title="Choose Embed Type" />
                  </div>
                )}

                {step === 1 && <StepTwoSections />}

                {step === 2 && (
                  <Suspense fallback={null}>
                    <InstallStep integrationId={createdIntegrationId} />
                  </Suspense>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  {renderActions()}
                </div>
              </div>
            </Form>
          </Formik>
        </IntegrationContext.Provider>
      </div>
    </RequireRole>
  )
}

export default IntegrationNewPage
